import { type ReactNode, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { Role } from '@ankor-io/common/types/account'
import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { FeatureToggleName, FeatureToggleValue } from '@ankor-io/common/types/system'

import App from '@/App'
import InvitationLandingPage from '@/pages/InvitationLanding'
import OnboardingPage from '@/pages/Onboarding'
import SignInPage from '@/pages/SignIn'
import SignUpPage from '@/pages/SignUp'
import APADashboardPage from '@/pages/apa/Dashboard'
import OPSDashboardPage from '@/pages/ops/Dashboard'
import { useFeatureToggle } from '@/providers/featureToggle/useFeatureToggle'
import { ModuleRedirect } from '@/routes/ModuleRedirect'
import { ProtectedRoute } from '@/routes/ProtectedRoute'

const ApprovalWorkflowsPage = lazy(() => import('@/pages/ApprovalWorkflows'))
const ExpenseApprovalWorkflowEditorPage = lazy(() => import('@/pages/ExpenseApprovalWorkflowEditor'))
const BeneficiariesPage = lazy(() => import('@/pages/Beneficiaries'))
const InitiatePaymentBeneficiariesPage = lazy(() => import('@/pages/InitiatePaymentBeneficiaries'))
const CreatePayoutTransferPage = lazy(() => import('@/pages/CreatePayoutTransfer'))
const PayoutTransferConfirmationPage = lazy(() => import('@/pages/PayoutTransferConfirmation'))
const PayBeneficiaryPage = lazy(() => import('@/pages/PayBeneficiary'))
const Forbidden = lazy(() => import('@/pages/Forbidden'))
const GlobalAccountDetailsPage = lazy(() => import('@/pages/GlobalAccountDetails'))
const KYCRFIPage = lazy(() => import('@/pages/KYCRFI'))
const NewBeneficiaryPage = lazy(() => import('@/pages/NewBeneficiary'))
const EditBeneficiaryPage = lazy(() => import('@/pages/EditBeneficiary'))
const NewConversionPage = lazy(() => import('@/pages/NewConversion'))
const NotFound = lazy(() => import('@/pages/NotFound'))
const OrganizationUsersPage = lazy(() => import('@/pages/OrganizationUsers'))
const VesselUsersPage = lazy(() => import('@/pages/VesselUsers'))
const ApaOwesOpsPage = lazy(() => import('@/pages/apa/ApaOwesOps'))
const APACardEditPage = lazy(() => import('@/pages/apa/CardEdit'))
const APACardholdersPage = lazy(() => import('@/pages/apa/Cardholders'))
const APACardsPage = lazy(() => import('@/pages/apa/Cards'))
const APACategoriesPage = lazy(() => import('@/pages/apa/Categories'))
const APACharterDetailPage = lazy(() => import('@/pages/apa/CharterDetail'))
const APACharterSignOffPage = lazy(() => import('@/pages/apa/CharterSignOff'))
const APACharterSignedOffPage = lazy(() => import('@/pages/apa/CharterSignedOff'))
const APAChartersPage = lazy(() => import('@/pages/apa/Charters'))
const APADownloadExpenseReportsPage = lazy(() => import('@/pages/apa/DownloadExpenseReports'))
const APADownloadFinancialReportsPage = lazy(() => import('@/pages/apa/DownloadFinancialReports'))
const APAEditCardholderPage = lazy(() => import('@/pages/apa/EditCardholder'))
const APAEditCharterPage = lazy(() => import('@/pages/apa/EditCharter'))
const APAEditTransactionPage = lazy(() => import('@/pages/apa/EditTransaction'))
const APAEditTransactionInspectPage = lazy(() => import('@/pages/apa/EditTransactionInspect'))
const APAIssueCardPage = lazy(() => import('@/pages/apa/IssueCard'))
const APANewCharterPage = lazy(() => import('@/pages/apa/NewCharter'))
const APANewGlobalAccountPage = lazy(() => import('@/pages/apa/NewGlobalAccount'))
const APANewTransactionPage = lazy(() => import('@/pages/apa/NewTransaction'))
const APAReportsPage = lazy(() => import('@/pages/apa/Reports'))
const APATransactionDetailPage = lazy(() => import('@/pages/apa/TransactionDetail'))
const APATransactionsPage = lazy(() => import('@/pages/apa/Transactions'))
const APAViewCardDetailsPage = lazy(() => import('@/pages/apa/ViewCardDetails'))
const APAWalletPage = lazy(() => import('@/pages/apa/Wallet'))
const OPSCardEditPage = lazy(() => import('@/pages/ops/CardEdit'))
const OPSCardholdersPage = lazy(() => import('@/pages/ops/Cardholders'))
const OPSCardsPage = lazy(() => import('@/pages/ops/Cards'))
const OPSCashPage = lazy(() => import('@/pages/ops/Cash'))
const OPSCashDetailPage = lazy(() => import('@/pages/ops/CashDetail'))
const OPSCategoriesPage = lazy(() => import('@/pages/ops/Categories'))
const OPSDownloadExpenseReportsPage = lazy(() => import('@/pages/ops/DownloadExpenseReports'))
const OPSDownloadFinancialReportsPage = lazy(() => import('@/pages/ops/DownloadFinancialReports'))
const OPSDownloadXeroReportsPage = lazy(() => import('@/pages/ops/DownloadXeroReports'))
const OPSEditCardholderPage = lazy(() => import('@/pages/ops/EditCardholder'))
const OPSEditTransactionPage = lazy(() => import('@/pages/ops/EditTransaction'))
const OPSEditTransactionInspectPage = lazy(() => import('@/pages/ops/EditTransactionInspect'))
const OPSIssueCardPage = lazy(() => import('@/pages/ops/IssueCard'))
const OPSNewGlobalAccountPage = lazy(() => import('@/pages/ops/NewGlobalAccount'))
const OPSNewTransactionPage = lazy(() => import('@/pages/ops/NewTransaction'))
const OPSReportsPage = lazy(() => import('@/pages/ops/Reports'))
const OPSTransactionDetailPage = lazy(() => import('@/pages/ops/TransactionDetail'))
const OPSTransactionsPage = lazy(() => import('@/pages/ops/Transactions'))
const OPSViewCardDetailsPage = lazy(() => import('@/pages/ops/ViewCardDetails'))
const OPSWalletPage = lazy(() => import('@/pages/ops/Wallet'))
const IntegrationsPage = lazy(() => import('@/pages/settings/integrations/Integrations'))
const QuickBooksPage = lazy(() => import('@/pages/settings/integrations/QuickBooks'))
const QuickBooksFeatures = lazy(() => import('@/pages/settings/integrations/QuickBooksFeatures'))
const XeroExpensesPage = lazy(() => import('@/pages/settings/integrations/XeroExpenses'))
const XeroExpensesFeatures = lazy(() => import('@/pages/settings/integrations/XeroExpensesFeatures'))
const SCAManagementPage = lazy(() => import('@/pages/settings/SCAManagement'))
const OrganizationActiveUsers = lazy(() => import('@/views/OrganizationUsers/ActiveUsers'))
const OrganizationInvites = lazy(() => import('@/views/OrganizationUsers/Invites'))
const VesselUsersActiveUsers = lazy(() => import('@/views/VesselUsers/ActiveUsers'))
const VesselUsersInvites = lazy(() => import('@/views/VesselUsers/Invites'))
const APADeposits = lazy(() => import('@/views/apa/Wallet/Deposits'))
const APAGlobalAccounts = lazy(() => import('@/views/apa/Wallet/GlobalAccounts'))
const APAWalletTransactionsPage = lazy(() => import('@/views/apa/Wallet/Transactions'))
const OPSDeposits = lazy(() => import('@/views/ops/Wallet/Deposits'))
const OPSGlobalAccounts = lazy(() => import('@/views/ops/Wallet/GlobalAccounts'))
const OPSWalletTransactionsPage = lazy(() => import('@/views/ops/Wallet/Transactions'))
const QuickBooksEntityMappingTab = lazy(() => import('@/views/settings/integrations/QuickBooks/EntityMappingTab'))
const QuickBooksChartOfAccountsTab = lazy(() => import('@/views/settings/integrations/QuickBooks/ChartOfAccountsTab'))
const QuickBooksCurrencyConfigurationTab = lazy(
  () => import('@/views/settings/integrations/QuickBooks/CurrencyConfigurationTab'),
)
const QuickBooksTaxRatesTab = lazy(() => import('@/views/settings/integrations/QuickBooks/TaxRatesTab'))
const ChartOfAccountsTab = lazy(() => import('@/views/settings/integrations/XeroExpenses/ChartOfAccountsTab'))
const CurrencyConfigurationTab = lazy(
  () => import('@/views/settings/integrations/XeroExpenses/CurrencyConfigurationTab'),
)
const EntityMappingTab = lazy(() => import('@/views/settings/integrations/XeroExpenses/EntityMappingTab'))
const TaxRatesTab = lazy(() => import('@/views/settings/integrations/XeroExpenses/TaxRatesTab'))
const TrackingCategoriesTab = lazy(() => import('@/views/settings/integrations/XeroExpenses/TrackingCategoriesTab'))

const BeneficiaryPayPageRoute = () => {
  const { orgFeatureToggles } = useFeatureToggle()
  const payoutTransferEnabled =
    orgFeatureToggles?.featureToggles?.[FeatureToggleName.PAYOUT_TRANSFER] === FeatureToggleValue.ENABLED

  return payoutTransferEnabled ? <CreatePayoutTransferPage /> : <PayBeneficiaryPage />
}

const APAEditTransactionRoute = () => {
  const { orgFeatureToggles } = useFeatureToggle()
  const transactionEditEnabled =
    orgFeatureToggles?.featureToggles?.[FeatureToggleName.TRANSACTION_EDIT] === FeatureToggleValue.ENABLED

  return transactionEditEnabled ? <APAEditTransactionInspectPage /> : <APAEditTransactionPage />
}

const QuickBooksRoute = ({ children }: { children: ReactNode }) => {
  const { orgFeatureToggles } = useFeatureToggle()
  const quickBooksEnabled =
    orgFeatureToggles?.featureToggles?.[FeatureToggleName.QUICKBOOKS_INTEGRATION] === FeatureToggleValue.ENABLED

  if (!quickBooksEnabled) {
    return (
      <Navigate
        to="/settings/integrations"
        replace
      />
    )
  }

  return children
}

const OPSEditTransactionRoute = () => {
  const { orgFeatureToggles } = useFeatureToggle()
  const transactionEditEnabled =
    orgFeatureToggles?.featureToggles?.[FeatureToggleName.TRANSACTION_EDIT] === FeatureToggleValue.ENABLED

  return transactionEditEnabled ? <OPSEditTransactionInspectPage /> : <OPSEditTransactionPage />
}

const Router = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth Pages */}
        <Route
          path="/sign-in"
          element={<SignInPage />}
        />

        <Route
          path="/sign-up"
          element={<SignUpPage />}
        />

        <Route
          path="/invite/:inviteId"
          element={<InvitationLandingPage />}
        />

        <Route
          path="/onboarding"
          element={<OnboardingPage />}
        />

        <Route element={<App />}>
          {/* Root redirect — routes to the correct module dashboard */}
          <Route
            path="/"
            element={<ModuleRedirect />}
          />

          {/* ------------------------ */}
          {/* Non-module related Pages */}
          {/* ------------------------ */}
          <Route
            path="/forbidden"
            element={<Forbidden />}
          />

          <Route
            path="/kyc-rfi"
            element={
              <ProtectedRoute minRole={Role.ADMIN}>
                <KYCRFIPage />
              </ProtectedRoute>
            }
          />

          {/* Organization Users Pages */}
          <Route
            path="/users"
            element={
              <ProtectedRoute minRole={Role.ADMIN}>
                <OrganizationUsersPage />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Navigate
                  to="list"
                  replace
                />
              }
            />
            <Route
              path="list"
              element={<OrganizationActiveUsers />}
            />
            <Route
              path="invites"
              element={<OrganizationInvites />}
            />
          </Route>

          {/* Settings > Integrations */}
          <Route
            path="/settings/integrations"
            element={
              <ProtectedRoute minRole={Role.ADMIN}>
                <IntegrationsPage />
              </ProtectedRoute>
            }
          />

          {/* Xero Expenses Features */}
          <Route
            path="/settings/integrations/xero-expenses/features"
            element={
              <ProtectedRoute minRole={Role.ADMIN}>
                <XeroExpensesFeatures />
              </ProtectedRoute>
            }
          />

          {/* QuickBooks Features */}
          <Route
            path="/settings/integrations/quickbooks/features"
            element={
              <ProtectedRoute minRole={Role.ADMIN}>
                <QuickBooksRoute>
                  <QuickBooksFeatures />
                </QuickBooksRoute>
              </ProtectedRoute>
            }
          />

          {/* Xero Expenses */}
          <Route
            path="/settings/integrations/xero-expenses"
            element={
              <ProtectedRoute minRole={Role.ADMIN}>
                <XeroExpensesPage />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Navigate
                  to="entity-mapping"
                  replace
                />
              }
            />
            <Route
              path="entity-mapping"
              element={<EntityMappingTab />}
            />
            <Route
              path="currency"
              element={<CurrencyConfigurationTab />}
            />
            <Route
              path="chart-of-accounts"
              element={<ChartOfAccountsTab />}
            />
            <Route
              path="tax-rates"
              element={<TaxRatesTab />}
            />
            <Route
              path="tracking-categories"
              element={<TrackingCategoriesTab />}
            />
          </Route>

          {/* QuickBooks */}
          <Route
            path="/settings/integrations/quickbooks"
            element={
              <ProtectedRoute minRole={Role.ADMIN}>
                <QuickBooksRoute>
                  <QuickBooksPage />
                </QuickBooksRoute>
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Navigate
                  to="entity-mapping"
                  replace
                />
              }
            />
            <Route
              path="entity-mapping"
              element={<QuickBooksEntityMappingTab />}
            />
            <Route
              path="currency"
              element={<QuickBooksCurrencyConfigurationTab />}
            />
            <Route
              path="chart-of-accounts"
              element={<QuickBooksChartOfAccountsTab />}
            />
            <Route
              path="tax-rates"
              element={<QuickBooksTaxRatesTab />}
            />
          </Route>

          {/* SCA Management available to every authenticated user */}
          <Route
            path="/settings/sca-management"
            element={<SCAManagementPage />}
          />

          {/* Vessel Users Pages */}
          <Route
            path="/vessel-users"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <VesselUsersPage />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Navigate
                  to="list"
                  replace
                />
              }
            />
            <Route
              path="list"
              element={<VesselUsersActiveUsers />}
            />
            <Route
              path="invites"
              element={<VesselUsersInvites />}
            />
          </Route>

          {/* Beneficiaries Pages */}
          <Route
            path="/transactions/:transactionId/initiate-payment"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <InitiatePaymentBeneficiariesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/:transactionId/beneficiaries"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <BeneficiariesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/:transactionId/beneficiaries/new"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <NewBeneficiaryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/:transactionId/beneficiaries/:beneficiaryId/edit"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <EditBeneficiaryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/:transactionId/beneficiaries/:beneficiaryId/pay"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <BeneficiaryPayPageRoute />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/:transactionId/beneficiaries/:beneficiaryId/pay/confirmation"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <PayoutTransferConfirmationPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/beneficiaries"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <BeneficiariesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/beneficiaries/new"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <NewBeneficiaryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/beneficiaries/:beneficiaryId/edit"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <EditBeneficiaryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/beneficiaries/:beneficiaryId/pay"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <BeneficiaryPayPageRoute />
              </ProtectedRoute>
            }
          />

          <Route
            path="/beneficiaries/:beneficiaryId/pay/confirmation"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <PayoutTransferConfirmationPage />
              </ProtectedRoute>
            }
          />

          {/* Global Accounts Pages */}
          <Route
            path="/wallet/global-accounts/:globalAccountId"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <GlobalAccountDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/wallet/conversions/new"
            element={
              <ProtectedRoute minRole={Role.VESSEL_ADMIN}>
                <NewConversionPage />
              </ProtectedRoute>
            }
          />

          {/* --------- */}
          {/* APA Pages */}
          {/* --------- */}

          {/* Dashboard */}
          <Route
            path="/apa/dashboard"
            element={
              <ProtectedRoute
                minRole={Role.VIEWER}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APADashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Cards Pages */}
          <Route
            path="/apa/cards"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APACardsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/cards/new/:cardholderId?"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APAIssueCardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/cards/card-edit/:cardId"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APACardEditPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/cards/card-details/:cardId"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APAViewCardDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/cards/cardholders"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APACardholdersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/cards/cardholders/:cardholderId/edit"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APAEditCardholderPage />
              </ProtectedRoute>
            }
          />

          {/* Charter Pages */}
          <Route
            path="/apa/charters"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
                requireVesselAccess
              >
                <APAChartersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/charters/:charterId"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
                requireVesselAccess
              >
                <APACharterDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/charters/new"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
                requireVesselAccess
              >
                <APANewCharterPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/charters/:charterId/edit"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
                requireVesselAccess
              >
                <APAEditCharterPage />
              </ProtectedRoute>
            }
          />

          {/* Create new transaction page without the charter id */}
          <Route
            path="/apa/transactions/new"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APANewTransactionPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/charters/:charterId/transactions/new"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APANewTransactionPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/charters/:charterId/sign-off"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
                requireVesselAccess
              >
                <APACharterSignOffPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/charters/:charterId/signed-off"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
                requireVesselAccess
              >
                <APACharterSignedOffPage />
              </ProtectedRoute>
            }
          />

          {/* Global Level Transactions Pages */}
          <Route
            path="/apa/transactions"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APATransactionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/transactions/:transactionId/edit"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APAEditTransactionRoute />
              </ProtectedRoute>
            }
          />

          {/* Transaction Detail Page */}
          <Route
            path="/apa/transactions/:transactionId/details"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APATransactionDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/approval-workflows"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <ApprovalWorkflowsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/approval-workflows/expense/new"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <ExpenseApprovalWorkflowEditorPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/approval-workflows/expense/:workflowId/edit"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <ExpenseApprovalWorkflowEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Wallet Pages */}
          <Route
            path="/apa/wallet"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APAWalletPage />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Navigate
                  to="global-accounts"
                  replace
                />
              }
            />
            <Route
              path="global-accounts"
              element={<APAGlobalAccounts />}
            />
            <Route
              path="transactions"
              element={<APAWalletTransactionsPage />}
            />
            <Route
              path="deposits"
              element={<APADeposits />}
            />
          </Route>

          <Route
            path="/apa/wallet/global-accounts/new"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APANewGlobalAccountPage />
              </ProtectedRoute>
            }
          />

          {/* Reports */}
          <Route
            path="/apa/reports"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APAReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/reports/download-financial-reports"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APADownloadFinancialReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/apa/reports/download-expense-reports"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APADownloadExpenseReportsPage />
              </ProtectedRoute>
            }
          />

          {/* APA Categories */}
          <Route
            path="/apa/categories"
            element={
              <ProtectedRoute
                minRole={Role.ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <APACategoriesPage />
              </ProtectedRoute>
            }
          />

          {/* APA Owes Operations Pages */}
          <Route
            path="/apa/apa-owes-ops"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.APA}
              >
                <ApaOwesOpsPage />
              </ProtectedRoute>
            }
          />

          {/* ---------------- */}
          {/* End of APA Pages */}
          {/* ---------------- */}

          {/* --------- */}
          {/* OPS Pages */}
          {/* --------- */}

          {/* Dashboard */}
          <Route
            path="/ops/dashboard"
            element={
              <ProtectedRoute
                minRole={Role.VIEWER}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Cards Pages */}
          <Route
            path="/ops/cards"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSCardsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/cards/new/:cardholderId?"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSIssueCardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/cards/card-edit/:cardId"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSCardEditPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/cards/card-details/:cardId"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSViewCardDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/cards/cardholders"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSCardholdersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/cards/cardholders/:cardholderId/edit"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSEditCardholderPage />
              </ProtectedRoute>
            }
          />

          {/* Global Level Transactions Pages */}
          <Route
            path="/ops/transactions"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSTransactionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/transactions/new"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSNewTransactionPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/transactions/:transactionId/edit"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSEditTransactionRoute />
              </ProtectedRoute>
            }
          />

          {/* Transaction Detail Page */}
          <Route
            path="/ops/transactions/:transactionId/details"
            element={
              <ProtectedRoute
                minRole={Role.SPENDER}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSTransactionDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/approval-workflows"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <ApprovalWorkflowsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/approval-workflows/expense/new"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <ExpenseApprovalWorkflowEditorPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/approval-workflows/expense/:workflowId/edit"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <ExpenseApprovalWorkflowEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Wallet Pages */}
          <Route
            path="/ops/wallet"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSWalletPage />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Navigate
                  to="global-accounts"
                  replace
                />
              }
            />
            <Route
              path="global-accounts"
              element={<OPSGlobalAccounts />}
            />
            <Route
              path="transactions"
              element={<OPSWalletTransactionsPage />}
            />
            <Route
              path="deposits"
              element={<OPSDeposits />}
            />
          </Route>

          <Route
            path="/ops/wallet/global-accounts/new"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSNewGlobalAccountPage />
              </ProtectedRoute>
            }
          />

          {/* Reports */}
          <Route
            path="/ops/reports"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/reports/download-financial-reports"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSDownloadFinancialReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/reports/download-xero-reports"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSDownloadXeroReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/reports/download-expense-reports"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSDownloadExpenseReportsPage />
              </ProtectedRoute>
            }
          />

          {/* Cash */}
          <Route
            path="/ops/cash"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSCashPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ops/cash/:cashFloatId"
            element={
              <ProtectedRoute
                minRole={Role.VESSEL_ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSCashDetailPage />
              </ProtectedRoute>
            }
          />

          {/* OPS Categories */}
          <Route
            path="/ops/categories"
            element={
              <ProtectedRoute
                minRole={Role.ADMIN}
                requiredModule={AwxConnectedAccountType.OPERATIONS}
              >
                <OPSCategoriesPage />
              </ProtectedRoute>
            }
          />

          {/* ---------------- */}
          {/* End of OPS Pages */}
          {/* ---------------- */}

          {/* 404 Catch-all Route - Must be last */}
          <Route
            path="*"
            element={<NotFound />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export default Router
